import { inject, Injectable } from "@angular/core";

import { LoggerService } from "./logger.service";
import { ApplicationError, ClientError, HTTPError, isSessionInvalidError } from "./exception.service";
import { GeneralConfig, EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { ApplicationHttpResponse } from "sharedclasses";
import { BaseLoginInfo } from "src/app/global-service/user.service";
import { AuthErrorInterceptorService } from "src/app/global-service/auth-error.interceptor";
import { TokenStorageService } from "src/app/global-service/token-storage.service";
import { clearClientSession } from "src/app/global-service/session-cleanup";
import { Router } from "@angular/router";
import { UrlCollection } from "src/app/app-routing.module";

/**
 * Estrae il nome file dall'header HTTP Content-Disposition (RFC 6266, filename* RFC 5987).
 * Evita split fragili su `;` che producono nomi corrotti (es. estensione `.pdf_`).
 * @param contentDisposition - Valore grezzo dell'header Content-Disposition
 * @returns Nome file decodificato oppure undefined se assente
 */
function parseFileNameFromContentDisposition(contentDisposition: string | null): string | undefined {
  if (!contentDisposition?.trim()) {
    return undefined;
  }
  const header = contentDisposition.trim();

  const starMatch = /filename\*=(?:UTF-8''|utf-8'')([^;\n]+)/i.exec(header);
  if (starMatch?.[1]) {
    const encoded = starMatch[1].trim().replace(/^["']|["']$/g, "");
    try {
      return decodeURIComponent(encoded.replace(/\+/g, " "));
    } catch {
      return encoded;
    }
  }

  const quotedMatch = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(header);
  if (quotedMatch?.[1]) {
    return quotedMatch[1].replace(/\\(.)/g, "$1");
  }

  const plainMatch = /filename\s*=\s*([^;\n]+)/i.exec(header);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim().replace(/^["']|["']$/g, "");
  }

  return undefined;
}

/**
 * Normalizza il nome file per l'attributo `download` (CR/LF, underscore dopo estensione).
 * @param name - Nome file eventualmente sporco
 * @returns Nome utilizzabile per il salvataggio
 */
function normalizeDownloadFileName(name: string | undefined): string {
  let n = name?.trim() ?? "";
  n = n.replace(/[\r\n\0]/g, "");
  n = n.replace(/(\.[a-z0-9]{1,10})_+$/i, "$1");
  n = n.trim();
  return n.length > 0 ? n : "download";
}

export type RequestMethod = "get" | "post" | "put" | "delete";

export type ApplicationResponse<T> = [T | undefined, ClientError | undefined];

export namespace API_REQUEST_METHODS {
  export const GET = "get";
  export const POST = "post";
  export const PUT = "put";
  export const DELETE = "delete";
}

export interface FetchArguments {
  method?: RequestMethod,
  url?: string,
  path?: string[];
  query?: Record<string, unknown> | object;
  body?: Record<string, unknown> | object;
  header?: Record<string, string> | object;
  timeout?: number;
  retry?: number;
  formData?: boolean;
  noFlickering?: boolean;
  minimum?: number;
  isFileDownload?: boolean;
}

@Injectable({ providedIn: "root" })
export abstract class HttpService {
  constructor() {
    this.logger.setCaller("HttpService");
  }
  protected envConfig: EnvironmentLoaderService = inject(EnvironmentLoaderService);
  protected logger: LoggerService = inject(LoggerService);
  protected authErrorInterceptor: AuthErrorInterceptorService = inject(AuthErrorInterceptorService);
  protected env: GeneralConfig = this.envConfig.getEnvConfig();
  protected tokenStorage: TokenStorageService = inject(TokenStorageService);
  protected router: Router = inject(Router);
  protected tokenKey: string = this.env.localTokenKey;
  protected flickeringTimeout?: ReturnType<typeof setTimeout>;
  protected instance?: {
    url?: string;
    header?: Record<string, string>;
    noFlickering?: boolean;
    minimum?: number;
  };

  /**
   * Sessione o token non validi: pulizia storage e redirect al login.
   * Non lancia eccezioni dopo la navigazione (evita toast/redirect doppi mentre il router naviga).
   * @param response - Corpo applicativo della risposta HTTP
   * @returns true se l'errore è stato gestito con redirect (il chiamante non deve propagare ClientError)
   */
  private handleAuthNotValid(response: ApplicationHttpResponse<unknown>): boolean {
    if (!response) {
      return false;
    }
    const hasError = !!response.error;
    if (!hasError) {
      return false;
    }

    // La decisione sta su CODICE HTTP e codice applicativo, non sul testo del messaggio: la
    // regola è una sola per tutta l'applicazione, vedi `isSessionInvalidError`. Prima qui c'era un
    // elenco di tre messaggi, e i tre messaggi del controllo CSRF non erano fra quelli.
    if (!isSessionInvalidError({ applicationError: response.error, httpErrorCode: response.httpErrorCode, messageFromError: response.messageFromError })) {
      return false;
    }

    const tokenKey = this.env.loginType === "local" ? this.env.localTokenKey : this.env.ssoTokenKey;
    // Pulisci lo stato locale per evitare tentativi futuri con una sessione non valida
    clearClientSession(this.tokenStorage, tokenKey);
    // Reindirizza alla pagina di login
    this.logger.error("Authorization not valid, redirecting to login page");
    void this.router.navigate([UrlCollection.localAuthentication]);
    return true;
  }

  /**
   * Setta nel service alcune informazioni per eseguire le chiamate HTTP,
   * senza doverle riportare ogni volta nei parametri della richiesta
   * @param instance
   */
  protected createInstance(instance: {
    url?: string;
    header?: Record<string, string>;
    noFlickering?: boolean;
    minimum?: number;
  }) {
    this.instance = {
      header: instance.header,
      url: instance.url,
      noFlickering: instance.noFlickering,
      minimum: instance.minimum
    };
  }
  /**
   * Esegue una chiamata REST con path, header, token e opzioni di rete/flickering.
   *
   * **Contratto su `ApplicationHttpResponse.response`**: solo `undefined` indica “nessun campo risposta”
   * e, dopo eventuale redirect per sessione non valida (`handleAuthNotValid`), comporta {@link ClientError}.
   * Un JSON `null` esplicito per `response` resta un payload valido e viene restituito tale e quale; i
   * chiamanti che non ammettono `null` devono gestirlo lato servizio.
   *
   * @param service - Segmento servizio sul path (combinato con base URL dell'istanza)
   * @param fetchArguments - Metodo, body, query, retry, download file, ecc.
   * @returns Dato tipizzato `T` dalla risposta applicativa
   * @throws {ClientError} Se `response` è `undefined` (dopo controllo auth) o payload non valido
   * @throws {ApplicationError} Incapsula errori di rete o eccezioni non gestite
   */
  protected async httpClient<T>(service: string, fetchArguments?: FetchArguments): Promise<T> {
    // Lista dei servizi da escludere dal circuit breaker 
    // (servizi di autenticazione che devono passare sempre)
    /* const allowedDuringLogout = [
      "authn/login",
      "authn/mfa-verify", 
      "authn/refresh-token",
      "authn/decode-single-use-token"
    ]; */

    const { method, body, header, url, query: queryParam, path: pathParam, retry, formData, timeout, minimum, isFileDownload } = fetchArguments ?? {};
    // by default enable no flickering
    const noFlickering = fetchArguments?.noFlickering ?? true;

    // rimuovo null e undefined valori dai queryparams
    const filteredQueryParam = queryParam ? Object.fromEntries(
      Object.entries(queryParam).filter(([, value]) => value !== null && value !== undefined)
    ) : undefined;
    /** Prepara il path e gli headers */
    const path = this.createPath(url ?? this.instance?.url ?? "", service, filteredQueryParam, pathParam);
    let headers = this.createHeaders(header, formData);

    if (this.instance?.header) {
      const headersFromInstance = this.createHeaders(this.instance.header, formData);
      headers = { ...headersFromInstance, ...headers };
    }

    if (!path) {
      throw new Error("Url mancante");
    }

    try {
      const applicationHttpResponse = await this.prepareHttpRequest<T>(path, headers, method, body, formData, timeout, retry, noFlickering, minimum, isFileDownload);

      // Intenzionale: non usare `response === null`. `null` da JSON è un valore di risposta lecito;
      // solo `undefined` segnala assenza di payload e attiva ClientError dopo handleAuthNotValid.
      if (applicationHttpResponse?.response === undefined) {
        if (applicationHttpResponse?.error && this.handleAuthNotValid(applicationHttpResponse)) {
          return undefined as T;
        }
        throw new ClientError(applicationHttpResponse);
      }

      if (applicationHttpResponse.error) {
        if (this.handleAuthNotValid(applicationHttpResponse)) {
          return applicationHttpResponse.response as T;
        } else {
          throw new ClientError(applicationHttpResponse);
        }
      }

      return applicationHttpResponse.response;

    } catch (error: unknown) {
      // Lista dei servizi da escludere dall'intercettazione errori auth
      // (servizi di login/autenticazione che non devono causare logout automatico)
      const excludedServices = [
        "authn/login",           // Login iniziale
        "authn/mfa-verify",      // Verifica MFA
        "authn/refresh-token",   // Refresh token
        "authn/decode-single-use-token"  // Decode token SSO
      ];

      // Intercetta gli errori di autenticazione solo se non è un servizio escluso
      const shouldIntercept = !excludedServices.some(excludedService => 
        service.includes(excludedService)
      );

      if (shouldIntercept) {
        await this.authErrorInterceptor.interceptError(error, "HttpService", service);
      }
      
      throw new ApplicationError(error, method ?? API_REQUEST_METHODS.GET, service);

    }
  }

  private async prepareHttpRequest<T>(
    path: string,
    headers?: Record<string, string> | object,
    method?: RequestMethod,
    body?: Record<string, unknown> | object,
    formData?: boolean,
    timeout?: number,
    retry?: number,
    noFlickering?: boolean,
    minimum?: number,
    isFileDownload?: boolean
  ): Promise<ApplicationHttpResponse<T>> {
    switch (method) {
      case API_REQUEST_METHODS.POST:
        return this.postData<T>(path, headers, body, formData, timeout, retry, noFlickering, minimum);

      case API_REQUEST_METHODS.PUT:
        return this.putData<T>(path, headers, body, timeout, retry, noFlickering, minimum);

      case API_REQUEST_METHODS.DELETE:
        return this.deleteData<T>(path, headers, body, formData, timeout, retry, noFlickering, minimum);

      default:
        return this.getData<T>(path, headers, timeout, retry, noFlickering, minimum, isFileDownload);
    }
  }

  /** GET */
  private async getData<T>(
    url: string,
    headers?: Record<string, string> | object,
    timeout?: number,
    retry?: number,
    noFlickering?: boolean,
    minimum?: number,
    isFileDownload?: boolean
  ): Promise<ApplicationHttpResponse<T>> {
    const params: Record<string, unknown> = {};

    Object.assign(params, { method: API_REQUEST_METHODS.GET });
    Object.assign(params, { headers: headers });

    try {

      return await this.optimizedFetch<T>(url, params, timeout, retry, noFlickering, minimum, isFileDownload);

    } catch (error: unknown) {

      throw new HTTPError((error as Error).message);

    }
  }

  /** POST */
  private async postData<T>(
    url: string,
    headers?: Record<string, string> | object,
    body?: Record<string, unknown> | object,
    formData?: boolean,
    timeout?: number,
    retry?: number,
    noFlickering?: boolean,
    minimum?: number
  ): Promise<ApplicationHttpResponse<T>> {
    const params: Record<string, unknown> = {};

    if (this.isString(body) || formData) {
      Object.assign(params, { body: body });
    } else {
      Object.assign(params, { body: JSON.stringify(body) });
    }

    Object.assign(params, { method: API_REQUEST_METHODS.POST });
    Object.assign(params, { headers: headers });

    try {

      return await this.optimizedFetch<T>(url, params, timeout, retry, noFlickering, minimum);

    } catch (error: unknown) {

      throw new HTTPError((error as Error).message);


    }
  }

  /** PUT */
  private async putData<T>(
    url: string,
    headers?: Record<string, string> | object,
    body?: Record<string, unknown> | object,
    timeout?: number,
    retry?: number,
    noFlickering?: boolean,
    minimum?: number
  ): Promise<ApplicationHttpResponse<T>> {
    const params: Record<string, unknown> = {};

    if (this.isString(body)) {
      Object.assign(params, { body: body });
    } else {
      Object.assign(params, { body: JSON.stringify(body) });
    }

    Object.assign(params, { method: API_REQUEST_METHODS.PUT });
    Object.assign(params, { headers: headers });

    try {

      return await this.optimizedFetch<T>(url, params, timeout, retry, noFlickering, minimum);

    } catch (error: unknown) {

      throw new HTTPError((error as Error).message);

    }
  }

  /** DELETE */
  private async deleteData<T>(
    url: string,
    headers?: Record<string, string> | object,
    body?: Record<string, string> | object,
    formData?: boolean,
    timeout?: number,
    retry?: number,
    noFlickering?: boolean,
    minimum?: number
  ): Promise<ApplicationHttpResponse<T>> {
    const params: Record<string, unknown> = {};

    if (this.isString(body) || formData) {
      Object.assign(params, { body: body });
    } else {
      Object.assign(params, { body: JSON.stringify(body) });
    }

    Object.assign(params, { method: API_REQUEST_METHODS.DELETE });
    Object.assign(params, { headers: headers });

    try {

      return await this.optimizedFetch<T>(url, params, timeout, retry, noFlickering, minimum);

    } catch (error: unknown) {

      throw new HTTPError((error as Error).message);

    }
  }

  /**
   * Effettua una richiesta HTTP ottimizzata con gestione di timeout e ri-tentativi.
   *
   * @param {string} url URL della risorsa da richiedere.
   * @param {Record<string, unknown>} params Parametri della richiesta.
   * @param {number} [timeout] Timeout della richiesta in millisecondi.
   * @param {number} [retry] Numero massimo di ritentativi in caso di errore.
   * @returns {Promise<ApplicationHttpResponse<T>>} Una Promise che restituisce la risposta della richiesta.
   */
  private async optimizedFetch<T>(
    url: string,
    params: Record<string, unknown>,
    timeout?: number,
    retry?: number,
    noFlickering?: boolean,
    minimum?: number,
    isFileDownload?: boolean
  ): Promise<ApplicationHttpResponse<T>> {
    let response;

    try {
      if (this.flickeringTimeout) clearTimeout(this.flickeringTimeout);
      const start = window.performance.now();
      const controller = new AbortController();
      const abortFetch = timeout ? setTimeout(() => {
        controller.abort();
      }, timeout) : undefined;

      const init: Record<string, unknown> = { ...params, signal: controller.signal };
      // La sessione vive in un cookie HttpOnly emesso dal BACKEND (`authentication.token-in-cookie`),
      // e questo flag serve a farlo inviare. Prima era condizionato a `storeAccessToken === "cookies"`,
      // cioè a dove il FRONTEND tiene la propria copia delle informazioni di login: due cose diverse,
      // e la seconda non dice niente sulla prima. Finché l'API è same-origin il default di `fetch`
      // manda comunque i cookie, quindi lo sbaglio non si vedeva; il giorno in cui l'API stesse su
      // un'altra origine l'autenticazione smetterebbe di funzionare senza un motivo visibile.
      init["credentials"] = "include";

      response = await fetch(url, init);

      if (abortFetch) clearTimeout(abortFetch);

      if (isFileDownload) {
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        const rawName = parseFileNameFromContentDisposition(response.headers.get("Content-Disposition"));
        const filename = normalizeDownloadFileName(rawName);
        const blob = await response.blob();
        const fileUrl = URL.createObjectURL(blob);

        return {
          response: {
            fileName: filename,
            fileUrl: fileUrl
          }
        } as ApplicationHttpResponse<T>;
      }

      const jsonResponse = await response.json() as ApplicationHttpResponse<T>;

      const end = window.performance.now();

      // Questo controllo esiste per evitare un rapido "sfarfallio" del contenuto, nel caso in cui le dipendenze vengano recuperate troppo rapidamente.
      const DEFAULT_MIN_FLICKERING_TIME = this.env.flickeringServicesResponse;
      if (!noFlickering && end - start < (minimum ?? DEFAULT_MIN_FLICKERING_TIME)) {

        const minCalcTime = (minimum ?? DEFAULT_MIN_FLICKERING_TIME) - (end - start);
        return await new Promise(res =>
          this.flickeringTimeout = setTimeout(res, minCalcTime, jsonResponse)
        );
      } else {
        return jsonResponse;
      }
    } catch (error: unknown) {
      if (retry && retry > 0) {
        return await this.optimizedFetch(url, params, timeout, retry - 1);
      }

      throw new Error(response?.status.toString());
    }
  }


  /**
   *
   * Setta l'URL della chiamata in base ai parametri passati.
   *
   * @param baseURL
   * @param serviceName
   * @param params
   */
  protected createPath(baseURL: string, serviceName: string, queryParam?: Record<string, unknown> | object, pathParam?: string[]): string {
    /** Crea la prima parte del path, controllando se il baseURL ha già lo slash finale e in caso lo aggiunge */

    let path = baseURL + (baseURL.endsWith("/") ? "" : "/") + (serviceName.startsWith("/") ? serviceName.replace("/", "") : serviceName);

    /** Aggiunge al path eventuali urlParam, controllando se c'è già lo slash finale */
    if (pathParam) {
      path += (path.endsWith("/") ? "" : "/") + pathParam.join("/");
    }

    /** Aggiunge al path eventuali queryParam */
    if (!queryParam) {
      return path;
    }

    const QUERY_PARAMS = new URLSearchParams();
    /** Esegue il parse di ogni valore non string in tipo string */
    for (const [key, value] of Object.entries(queryParam)) {

      /** Se undefine non viene aggiunto il parametro al path */
      if (value === undefined) {
        continue;
      }

      /** Se viene passato un array */
      if (Array.isArray(value)) {
        /** Elimina tutti i valori falsy */
        const arr = value.filter(Boolean);

        for (const field of arr) {

          if (this.isDate(field)) {
            /** Nel caso sia una data viene convertita in UTC */
            QUERY_PARAMS.append(key, field.toISOString());
          }

          if (this.isString(field)) {
            /** Nel caso sia una stringa aggiunge semplicemente il valore al path */
            QUERY_PARAMS.append(key, field);
          }

          if (!this.isDate(field) && !this.isString(field)) {
            /** In tutti gli altri casi converti in stringa il valore */
            QUERY_PARAMS.append(key, JSON.stringify(field));
          }
        }

        continue;
      }

      if (this.isDate(value)) {
        /** Nel caso sia una data viene convertita in UTC */
        QUERY_PARAMS.append(key, value.toISOString());
      }

      if (this.isString(value)) {
        /** Nel caso sia una stringa aggiunge semplicemente il valore al path */
        QUERY_PARAMS.append(key, value);
      }

      if (!this.isDate(value) && !this.isString(value)) {
        /** In tutti gli altri casi converti in stringa il valore */
        QUERY_PARAMS.append(key, JSON.stringify(value));
      }
    }

    return path += "?" + QUERY_PARAMS.toString();
  }

  /**
   *
   * Setta l'header della chiamata.
   *
   * Se presente nei cookie ricevuti, viene inserito il CSRF (Cross Site Request Forgery) token per validare le chimaate.
   *
   * Se presente nello storage locale, viene aggiunto un token di autenticazione come Bearer. Se è come cookie non sarà inserito anche nell'header,
   *
   * @param header
   */
  private createHeaders(header?: Record<string, string> | object, formData?: boolean): Record<string, string> | undefined {
    const headers: Record<string, string> = { ...(header ?? {}) };

    if (!formData) {
      headers["Content-Type"] = "application/json";
    }

    // Se nei cookie c'e' il CSRF token, lo aggiunge all'header
    const csrfToken = this.getCookieValue(this.env.csrfTokenCookieName);
    if (csrfToken) {
      headers["x-csrf-token"] = csrfToken;
    }

    const loginInfo = this.token;
    if (loginInfo && this.isLoginInfo(loginInfo)) {
      headers["Authorization"] = `Bearer ${loginInfo.token}`;
    }

    return Object.keys(headers).length ? headers : undefined;
  }

  /**
   * Le informazioni di login dallo storage, o `undefined` se non ce ne sono.
   *
   * Delega a {@link TokenStorageService}, che è l'unico posto che sa in che FORMA il token è
   * salvato. Qui il cookie veniva riparsato a mano, con un `JSON.parse` non protetto: due copie
   * della stessa logica, di cui una sola al corrente che il valore ora è percent-codificato — e un
   * cookie illeggibile faceva lanciare un getter che sta sul percorso di ogni chiamata HTTP.
   */
  private get token(): BaseLoginInfo | undefined {
    const tokenKey: string | null = this.env.loginType === "local" ? this.env.localTokenKey : this.env.ssoTokenKey;
    if (!tokenKey) throw new ClientError({ error: "AUTH_TOKEN_KEY_MISSING" });
    const loginInfo = this.tokenStorage.retriveToken(tokenKey);
    // `retriveToken` risponde con un oggetto vuoto quando non c'è nulla: il contratto di questo
    // getter è `undefined`, e `isLoginInfo` lo scarterebbe comunque.
    return Object.keys(loginInfo ?? {}).length ? loginInfo : undefined;
  }

  /**
   *
   * Controlla se il parametro è una data
   *
   * @param date
   */
  private isDate(date: unknown): date is Date {
    return toString.call(date) === "[object Date]";
  }

  /**
   *
   * Controlla se il parametro è una string
   *
   * @param value
   */
  private isString(value: unknown): value is string {
    return typeof value === "string";
  }

  /**
   *
   * Controlla se l'oggetto passato è del tipo LoginInfo
   *
   * @param obj
   * @returns {boolean}
   */
  private isLoginInfo(obj: unknown): obj is BaseLoginInfo {
    if (typeof obj === "object" && obj)
      return obj && typeof obj === "object" && "expireDate" in obj && "jwtPayload" in obj && "token" in obj;
    return false;
  }

  protected isError(obj: unknown): obj is ClientError {
    return Object.prototype.toString.call(obj) === "[object Error]";
  }

  /**
   * Recupera il valore di un cookie dato il suo nome
   * @param name Il nome del cookie da recuperare
   * @returns Il valore del cookie o null se non trovato
   */
  protected getCookieValue(name: string): string | null {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(";");
    for (const element of cookies) {
      const cookie = element.trim();
      if (cookie.startsWith(nameEQ)) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }
    return null;
  }
}
